import { Interval, isWithinInterval } from 'date-fns';

import { Collision } from '@/features/collisions/types';
import { ScheduleAlert } from '@/features/scheduleAlerts/types';
import { isEqual } from '@/helpers/utils/objects';
import { NodeName } from '@/repositories/utils/cache';
import { checkIfEventIsFilteredOut } from '@/services/store/schedule/actions/filter';

import { isWorkerThresholdExceededAlert, WorkerThresholdExceededAlert } from '../capacities/types';
import { ScheduleAlertStore } from './scheduleAlertStore';

export function isAlertFiltered(scheduleAlert: ScheduleAlert | undefined) {
  if (!scheduleAlert) {
    return true;
  }

  if (isWorkerThresholdExceededAlert(scheduleAlert)) {
    return scheduleAlert.orderIds.every((orderId) =>
      checkIfEventIsFilteredOut(orderId, NodeName.ORDER),
    );
  }
  if (isCollision(scheduleAlert)) {
    return checkIfEventIsFilteredOut(scheduleAlert.toId, scheduleAlert.toType);
  }
  return false;
}

export function areAllAlertsOnSameDateFiltered(
  scheduleAlert: ScheduleAlert | undefined,
  scheduleAlertStore: ScheduleAlertStore,
) {
  if (!scheduleAlert) {
    return true;
  }

  const date = scheduleAlert?.date;

  const allAlerts = getScheduleAlertsForDate(scheduleAlertStore.scheduleAlerts, date!);
  return allAlerts.every((c) => isAlertFiltered(c));
}

export const getScheduleAlertsForDate = (
  scheduleAlerts: Map<string, ScheduleAlert>,
  date: Date,
) => {
  const alertsForDate: ScheduleAlert[] = [];
  scheduleAlerts.forEach((alert) => {
    if (isEqual(alert.date, date)) {
      alertsForDate.push(alert);
    }
  });
  return alertsForDate;
};

export const getScheduleAlertsForDateAsMap = (
  scheduleAlerts: Map<string, ScheduleAlert>,
  date: Date,
): Map<string, ScheduleAlert> => {
  const alertsOnDate: Map<string, ScheduleAlert> = new Map();
  scheduleAlerts.forEach((alert) => {
    if (isEqual(alert.date, date)) {
      alertsOnDate.set(alert.id, alert);
    }
  });
  return alertsOnDate;
};

export const isCollision = (alert?: ScheduleAlert): alert is Collision => {
  return Boolean(alert && 'dependencyId' in alert && alert.dependencyId);
};

export const findFirstScheduleAlertPerDay = (
  alertMap: Map<string, ScheduleAlert>,
): Map<string, ScheduleAlert> => {
  const getDateKey = (alert: ScheduleAlert): string => alert.date.toISOString();

  const alreadyFoundKeys = new Set<string>();
  const uniqueAlerts = new Map<string, ScheduleAlert>();

  alertMap.forEach((alert) => {
    const key = getDateKey(alert);
    if (alreadyFoundKeys.has(key)) return;

    alreadyFoundKeys.add(key);
    uniqueAlerts.set(alert.id, alert);
  });

  return uniqueAlerts;
};

export const findFirstScheduleAlertPerDayAndSectionAsCollision = (
  alertMap: Map<string, ScheduleAlert>,
): Map<string, Collision> => {
  const getDateKey = (collision: Collision): string =>
    `${collision.date.toISOString()}_${collision.wbsSectionId}`;

  const alreadyFoundKeys = new Set<string>();
  const uniqueCollisions = new Map<string, Collision>();

  alertMap.forEach((alert) => {
    if (!isCollision(alert)) return;

    const collision = alert;
    const key = getDateKey(collision);
    if (alreadyFoundKeys.has(key)) return;

    alreadyFoundKeys.add(key);
    uniqueCollisions.set(collision.id, collision);
  });

  return uniqueCollisions;
};

export const getWorkerThresholdExceededAlertsInInterval = (
  workerThresholdExceededAlerts: WorkerThresholdExceededAlert[],
  timeRange: Interval,
) => {
  return workerThresholdExceededAlerts.filter((alert) => isWithinInterval(alert.date, timeRange));
};

export const getScheduleAlertsByTarget = (
  scheduleAlerts: Map<string, ScheduleAlert>,
): Map<string, ScheduleAlert[]> => {
  const scheduleAlertsByTarget = new Map<string, ScheduleAlert[]>();
  scheduleAlerts.forEach((scheduleAlert) => {
    const key = isCollision(scheduleAlert) ? scheduleAlert.toId : 'collisions';
    const existing = scheduleAlertsByTarget.get(key);
    if (existing) {
      existing.push(scheduleAlert);
    } else {
      scheduleAlertsByTarget.set(key, [scheduleAlert]);
    }
  });
  return scheduleAlertsByTarget;
};

export function condenseWorkerThresholdAlerts(alerts: ScheduleAlert[]): ScheduleAlert[] {
  const thresholdAlerts: WorkerThresholdExceededAlert[] = [];
  const otherAlerts: ScheduleAlert[] = [];

  alerts.forEach((alert) => {
    if (isWorkerThresholdExceededAlert(alert)) {
      thresholdAlerts.push(alert);
    } else {
      otherAlerts.push(alert);
    }
  });

  if (thresholdAlerts.length === 0) {
    return alerts;
  }

  const firstThresholdAlert = thresholdAlerts[0];
  const condensedAlert: WorkerThresholdExceededAlert = {
    id: 'condensed-threshold-alert',
    actualWorkerCount: 0,
    date: firstThresholdAlert.date,
    orderIds: [],
    threshold: 0,
    title: firstThresholdAlert.title,
    type: 'contributorGroup',
  };

  return [...otherAlerts, condensedAlert];
}
